/**
 * MyTeachingExcellence — instructor-facing panel that surfaces the
 * Teaching Excellence breakdown from /api/teaching-excellence/me.
 *
 * Shows: current tier (or "not yet eligible"), mean score, review count,
 * gap to next tier, and recent review scores. Mobile-first.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Sparkles, Loader2, TrendingUp, Award, Info } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_STYLES = {
  bronze:   { ring: "ring-amber-200",  bg: "bg-amber-100   text-amber-800",  bar: "bg-amber-500" },
  silver:   { ring: "ring-slate-200",  bg: "bg-slate-200   text-slate-800",  bar: "bg-slate-500" },
  gold:     { ring: "ring-yellow-200", bg: "bg-yellow-100  text-yellow-800", bar: "bg-yellow-500" },
  platinum: { ring: "ring-cyan-200",   bg: "bg-cyan-100    text-cyan-700",   bar: "bg-cyan-500" },
  black:    { ring: "ring-slate-700",  bg: "bg-slate-900   text-white",      bar: "bg-slate-900" },
};

const TIER_LABEL = {
  bronze: "Bronze", silver: "Silver", gold: "Gold", platinum: "Platinum", black: "Black",
};

function nextTierGap(currentTier, score, thresholds) {
  const order = ["bronze", "silver", "gold", "platinum", "black"];
  const idx = currentTier ? order.indexOf(currentTier) : -1;
  const nextKey = order[idx + 1];
  if (!nextKey) return null;
  const target = thresholds?.[nextKey];
  if (typeof target !== "number" || typeof score !== "number") return null;
  return { tier: nextKey, label: TIER_LABEL[nextKey], target, gap: Math.max(0, +(target - score).toFixed(1)) };
}

export default function MyTeachingExcellence({ className = "" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/api/teaching-excellence/me`, { withCredentials: true });
        if (!cancelled) setData(d);
      } catch {
        /* silent — panel just hides */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const next = useMemo(
    () => (data ? nextTierGap(data.tier, data.score, data.thresholds) : null),
    [data]
  );

  if (loading) {
    return (
      <div className={`rounded-2xl border border-slate-200 bg-white p-4 ${className}`}>
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading Teaching Excellence…
        </div>
      </div>
    );
  }
  if (!data || data.enabled === false) return null;

  const tierStyle = data.tier ? TIER_STYLES[data.tier] : null;
  const score = typeof data.score === "number" ? data.score : null;

  return (
    <div data-testid="my-teaching-excellence" className={`rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 ${className}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-500" /> Teaching Excellence
        </h3>
        {data.tier && tierStyle ? (
          <span data-testid="my-te-tier" className={`inline-flex items-center gap-1 rounded-full ring-1 font-bold text-xs px-2.5 py-1 ${tierStyle.bg} ${tierStyle.ring}`}>
            <Award className="w-3 h-3" />
            {TIER_LABEL[data.tier]}
          </span>
        ) : (
          <span data-testid="my-te-tier" className="inline-flex items-center gap-1 rounded-full ring-1 ring-slate-200 bg-slate-50 text-slate-600 text-xs px-2.5 py-1">
            Not yet eligible
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <Stat label="Mean score" value={score == null ? "—" : score.toFixed(1)} testid="my-te-score" />
        <Stat label="Reviews counted" value={`${data.review_count}/${data.min_reviews}`} testid="my-te-count" />
      </div>

      {/* Progress to next tier */}
      {next && (
        <div className="mt-1" data-testid="my-te-next">
          <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
            <span className="inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Next tier — {next.label}</span>
            <span className="font-semibold">+{next.gap} to {next.target}</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className={`h-full ${tierStyle?.bar || "bg-blue-500"}`}
              style={{ width: `${Math.min(100, ((score || 0) / next.target) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {!data.eligible && (
        <p className="mt-3 text-xs text-slate-500 flex items-start gap-1.5">
          <Info className="w-3.5 h-3.5 flex-none mt-0.5" />
          Upload at least {data.min_reviews} AI lesson review{data.min_reviews === 1 ? "" : "s"} (within {data.window_days} days) to qualify.
        </p>
      )}

      {(data.breakdown || []).length > 0 && (
        <details className="mt-3">
          <summary className="text-xs font-semibold text-slate-600 cursor-pointer min-h-[36px] inline-flex items-center" data-testid="my-te-breakdown-toggle">
            Recent review scores
          </summary>
          <ul className="mt-2 space-y-1">
            {data.breakdown.map((b) => (
              <li key={b.review_id} className="flex items-center justify-between text-xs text-slate-600">
                <span className="truncate">{new Date(b.completed_at).toLocaleDateString()}</span>
                <span className="font-mono font-semibold text-slate-800">{b.score.toFixed(0)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-2.5">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div data-testid={testid} className="text-lg font-extrabold text-slate-900">{value}</div>
    </div>
  );
}
